import type { OrderSummaryBasket } from '@/components/order-summary/mobile-heading-utils';
import { getDeliveryServiceSurchargeAmounts } from '@/lib/floor-delivery';
import { getSoldByWeightSurchargeAmount } from '@/lib/sold-by-weight-surcharge';

export type OrderSummaryShippingSurcharges = {
    soldByWeight?: number;
    floorDelivery?: number;
    appointmentDelivery?: number;
};

function positiveAmount(value: number | undefined): number {
    return typeof value === 'number' && value > 0 ? value : 0;
}

/**
 * Resolves sold-by-weight and delivery-service surcharges already included in `shippingTotal`.
 */
export function getOrderSummaryShippingSurcharges(
    basket: OrderSummaryBasket | undefined
): OrderSummaryShippingSurcharges {
    const soldByWeight = getSoldByWeightSurchargeAmount(basket);
    const { floor, appointment } = getDeliveryServiceSurchargeAmounts(basket);

    return {
        ...(positiveAmount(soldByWeight) > 0 ? { soldByWeight } : {}),
        ...(positiveAmount(floor) > 0 ? { floorDelivery: floor } : {}),
        ...(positiveAmount(appointment) > 0 ? { appointmentDelivery: appointment } : {}),
    };
}

export function getSeparateShippingSurchargesTotal(surcharges: OrderSummaryShippingSurcharges): number {
    return (
        positiveAmount(surcharges.soldByWeight) +
        positiveAmount(surcharges.floorDelivery) +
        positiveAmount(surcharges.appointmentDelivery)
    );
}

/**
 * Base shipping cost shown in order summary and sent to GA4 checkout events.
 * Dedicated surcharge rows are excluded because they are tracked separately in UI.
 */
export function getDisplayShippingTotal(basket: OrderSummaryBasket | undefined): number | undefined {
    if (!basket || typeof basket.shippingTotal !== 'number') {
        return basket?.shippingTotal;
    }

    const separateShippingSurcharges = getSeparateShippingSurchargesTotal(getOrderSummaryShippingSurcharges(basket));

    if (separateShippingSurcharges > 0) {
        return Math.max(0, Math.round((basket.shippingTotal - separateShippingSurcharges) * 100) / 100);
    }

    return basket.shippingTotal;
}
